import React, { useEffect, useMemo, useState } from 'react';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import Spacer from 'components/Spacer';
import { Icon, Icons } from 'components/Icon';
import { BeneficiaryTypeEnum } from '../types/user.enum.type';
import CustomSelect from 'components/Form/CustomSelect';
import { RelationshipType } from 'constants/bank-account-type';
import { ExecutorType } from '../types/on-boarding.type';
import {
  useCreateExecutor,
  useDeleteExecutor,
  useGetExecutorList
} from 'common/queries-and-mutations/executor';
import { Loader } from 'components/Loader';
import { findNextPage, findPreviousPage, showRemoveButton } from 'utils';
import InfoBar from './InfoBar';
import { STEPS } from 'constants/steps';
import { fetchUserInformation } from 'thunks/account-thunk';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';

export default function ExecutorInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isLoading, isError } = useCreateExecutor();
  const { mutate: deleteExecutor, isLoading: isDeleting } = useDeleteExecutor();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    data,
    isSuccess: isExecutorSuccess,
    isLoading: isExecutorLoading
  } = useGetExecutorList();

  const executors = useMemo(() => {
    if (isExecutorSuccess && data.length > 0) {
      return data.map(executor => ({
        executorId: executor.id,
        executorFirstName: executor.executorFirstName,
        executorMiddleName: executor.executorMiddleName,
        executorLastName: executor.executorLastName?.trim(),
        executorType: executor.executorType,
        relationship: executor.relationship,
        address: executor.address,
        emailAddress: executor.emailAddress,
        phoneNumber: executor.phoneNumber,
        rcNumber: executor.rcNumber,
        incorporationDate: executor.incorporationDate,
        incorporationPlace: executor.incorporationPlace
      }));
    } else {
      return [
        {
          executorId: null,
          executorFirstName: '',
          executorMiddleName: '',
          executorLastName: '',
          executorType: '',
          relationship: '',
          address: '',
          emailAddress: '',
          phoneNumber: '',
          rcNumber: '',
          incorporationDate: '',
          incorporationPlace: ''
        }
      ];
    }
  }, [data, isExecutorSuccess]);

  const formik = useFormik({
    initialValues: {
      executors
    },
    validationSchema: Yup.object({
      executors: Yup.array().of(
        Yup.object().shape({
          executorFirstName: Yup.string().when('executorType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Executor first name is required')
              .nullable(),
            otherwise: Yup.string().required('Name is required').nullable()
          }),
          executorLastName: Yup.string().when('executorType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Executor last name is required')
              .nullable(),
            otherwise: Yup.string()
          }),
          executorType: Yup.string()
            .required('Executor type is required')
            .nullable(),
          address: Yup.string().required('Address is required').nullable(),

          emailAddress: Yup.string()
            .email('Invalid email address')
            .required('Email is required')
            .nullable(),
          rcNumber: Yup.string().when('executorType', {
            is: BeneficiaryTypeEnum.CORPORATE,
            then: Yup.string().required('RC Number is required').nullable(),
            otherwise: Yup.string()
          }),
          phoneNumber: Yup.string().when('executorType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string().required('Phone number is required').nullable(),
            otherwise: Yup.string()
          }),
          relationship: Yup.string().when('executorType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string().required('Relationship is required').nullable(),
            otherwise: Yup.string()
          })
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onExecutorSubmit(values);
    }
  });

  const addMoreExecutor = () => {
    formik.values.executors.push({
      executorId: null,
      executorFirstName: '',
      executorMiddleName: '',
      executorLastName: '',
      executorType: '',
      relationship: '',
      address: '',
      emailAddress: '',
      phoneNumber: '',
      rcNumber: '',
      incorporationDate: '',
      incorporationPlace: ''
    });
    formik.setFieldValue('executors', [...formik.values.executors]);
  };

  const removeExecutor = (
    indexToRemove: number,
    executorId?: number | null
  ) => {
    if (executorId) {
      deleteExecutor(executorId);
    }
    formik.values.executors.splice(indexToRemove, 1);
    formik.setFieldValue('executors', [...formik.values.executors]);
  };

  const onExecutorSubmit = (formValue: ExecutorType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.EXECUTOR_INFORMATION));
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, dispatch, navigate, planAllowPage]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return;
    }
    mutate(formik.values);
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.EXECUTOR_INFORMATION));
  };

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={1}
        stepPercentage={STEPS.EXECUTOR_INFORMATION}
        timelineStep={3}
      />
      {isExecutorLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
            Executor&apos;s Information
          </p>
          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {formik.values.executors.map((executor, index) => (
              <div key={index} className="mb-1 mt-8">
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`executors[${index}].executorFirstName`}
                      placeholder=""
                      value={executor.executorFirstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label={
                        executor.executorType === BeneficiaryTypeEnum.INDIVIDUAL
                          ? 'Executor First Name'
                          : 'Name'
                      }
                      error={
                        formik.touched.executors
                          ? formik.errors.executors?.[index] &&
                            formik.errors.executors.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.executors[index])
                              ).executorFirstName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <div className="relative mb-2 flex items-center justify-between">
                      <span className="flex items-center">
                        <label className="text-[12px] font-medium text-[#353535]">
                          Type of Executor
                        </label>
                        <div className="mx-2 mb-2">
                          <Spacer width={3} />
                          <Icon name={Icons.Required} />
                        </div>
                      </span>
                    </div>
                    <div className="mt-5 flex items-center gap-10">
                      <button
                        type="button"
                        onClick={() =>
                          formik.setFieldValue(
                            `executors[${index}].executorType`,
                            BeneficiaryTypeEnum.INDIVIDUAL
                          )
                        }
                        className="flex items-center gap-3"
                      >
                        <span className="text-base font-bold text-[#353535]">
                          Individual
                        </span>
                        <Icon
                          name={
                            executor.executorType ===
                            BeneficiaryTypeEnum.INDIVIDUAL
                              ? Icons.CheckedBox
                              : Icons.UncheckBox
                          }
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          formik.setFieldValue(
                            `executors[${index}].executorType`,
                            BeneficiaryTypeEnum.CORPORATE
                          )
                        }
                        className="flex items-center gap-3"
                      >
                        <span className="text-base font-bold text-[#353535]">
                          Corporate
                        </span>
                        <Icon
                          name={
                            executor.executorType ===
                            BeneficiaryTypeEnum.CORPORATE
                              ? Icons.CheckedBox
                              : Icons.UncheckBox
                          }
                        />
                      </button>
                    </div>
                  </div>
                </div>
                {executor.executorType === BeneficiaryTypeEnum.INDIVIDUAL && (
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`executors[${index}].executorMiddleName`}
                        placeholder=""
                        value={executor.executorMiddleName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Executor Middle Name"
                        error={
                          formik.touched.executors
                            ? formik.errors.executors?.[index] &&
                              formik.errors.executors.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.executors[index])
                                ).executorMiddleName
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`executors[${index}].executorLastName`}
                        placeholder=""
                        value={executor.executorLastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Executor Last Name"
                        error={
                          formik.touched.executors
                            ? formik.errors.executors?.[index] &&
                              formik.errors.executors.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.executors[index])
                                ).executorLastName
                              : ''
                            : ''
                        }
                        size="lg"
                        isRequired
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                )}
                {executor.executorType === BeneficiaryTypeEnum.INDIVIDUAL && (
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`executors[${index}].address`}
                        placeholder=""
                        isRequired
                        value={executor.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Home Address"
                        error={
                          formik.touched.executors
                            ? formik.errors.executors?.[index] &&
                              formik.errors.executors.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.executors[index])
                                ).address
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <CustomSelect
                        options={RelationshipType}
                        selectedOption={{
                          value: executor.relationship,
                          label: executor.relationship
                        }}
                        handleOptionChange={option => {
                          formik.setFieldValue(
                            `executors[${index}].relationship`,
                            option?.value
                          );
                        }}
                        label="Relationship"
                        name="relationship"
                        isRequired
                        isSearchable
                        className="h-[3.5rem] rounded-2xl"
                        error={
                          formik.touched.executors
                            ? formik.errors.executors?.[index] &&
                              formik.errors.executors.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.executors[index])
                                ).relationship
                              : ''
                            : ''
                        }
                      />
                    </div>
                  </div>
                )}
                {executor.executorType === BeneficiaryTypeEnum.CORPORATE && (
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`executors[${index}].rcNumber`}
                        placeholder=""
                        value={executor.rcNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="RC Number"
                        isRequired
                        error={
                          formik.touched.executors
                            ? formik.errors.executors?.[index] &&
                              formik.errors.executors.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.executors[index])
                                ).rcNumber
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`executors[${index}].address`}
                        placeholder=""
                        isRequired
                        value={executor.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Company Address"
                        error={
                          formik.touched.executors
                            ? formik.errors.executors?.[index] &&
                              formik.errors.executors.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.executors[index])
                                ).address
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                )}
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    {executor.executorType && (
                      <TextField
                        name={`executors[${index}].emailAddress`}
                        placeholder=""
                        isRequired
                        value={executor.emailAddress}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Email Address"
                        error={
                          formik.touched.executors
                            ? formik.errors.executors?.[index] &&
                              formik.errors.executors.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.executors[index])
                                ).emailAddress
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    )}
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    {executor.executorType ===
                      BeneficiaryTypeEnum.INDIVIDUAL && (
                      <TextField
                        name={`executors[${index}].phoneNumber`}
                        isRequired
                        placeholder=""
                        value={executor.phoneNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Phone Number"
                        error={
                          formik.touched.executors
                            ? formik.errors.executors?.[index] &&
                              formik.errors.executors.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.executors[index])
                                ).phoneNumber
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    )}
                    {executor.executorType ===
                      BeneficiaryTypeEnum.CORPORATE && (
                      <TextField
                        name={`executors[${index}].incorporationDate`}
                        type="date"
                        placeholder=""
                        value={executor.incorporationDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Date of Incorporation"
                        error={
                          formik.touched.executors
                            ? formik.errors.executors?.[index] &&
                              formik.errors.executors.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.executors[index])
                                ).incorporationDate
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    )}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    {executor.executorType ===
                      BeneficiaryTypeEnum.CORPORATE && (
                      <TextField
                        name={`executors[${index}].incorporationPlace`}
                        placeholder=""
                        value={executor.incorporationPlace}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Place of Incorporation"
                        error={
                          formik.touched.executors
                            ? formik.errors.executors?.[index] &&
                              formik.errors.executors.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.executors[index])
                                ).incorporationPlace
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    )}
                  </div>
                </div>
                {showRemoveButton(
                  index,
                  executor.executorId,
                  data!.length > 1
                ) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          removeExecutor(index, executor.executorId)
                        }
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between gap-2">
              <div className="flex w-full items-center gap-2">
                <Icon name={Icons.Required} />
                <span className="text-[10px] text-[#7A7C87]">
                  Indicates Required
                </span>
              </div>
              {formik.values.executors[0].executorType && (
                <div className="group z-0 flex w-full items-center justify-end">
                  <div
                    className="flex items-center gap-4"
                    onClick={addMoreExecutor}
                    role="button"
                    tabIndex={-1}
                  >
                    <Icon name={Icons.AddCircleOutline} />
                    <span className="mt-2 text-base font-medium text-[#353535]">
                      Add Another Executor
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-1 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-5 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type="submit"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
